import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Typography, Button, CircularProgress, Alert, Chip,
    IconButton, TextField, Dialog, DialogTitle, DialogContent,
    DialogActions, Tooltip, Avatar, Table, TableHead, TableBody,
    TableRow, TableCell,
} from "@mui/material";
import {
    CheckCircle as ApproveIcon,
    Cancel as DenyIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    PersonOff as PersonOffIcon,
} from "@mui/icons-material";
import {
    getAuthorisedUsers,
    approveAuthorisedUser,
    denyAuthorisedUser,
    deleteAuthorisedUser,
} from "../../services/tenantService";

const AuthorisedUsersList = ({ propertyId, onCountChange }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [actionLoading, setActionLoading] = useState({}); // email → true/false

    // Deny dialog
    const [denyDialogOpen, setDenyDialogOpen] = useState(false);
    const [denyTarget, setDenyTarget] = useState(null);   // user object
    const [denyReason, setDenyReason] = useState("");
    const [denySubmitting, setDenySubmitting] = useState(false);

    // Delete confirm dialog
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);

    // Snackbar-style inline feedback
    const [feedback, setFeedback] = useState(null); // { type, message }

    const showFeedback = (type, message) => {
        setFeedback({ type, message });
        setTimeout(() => setFeedback(null), 4000);
    };

    const fetchUsers = useCallback(async () => {
        if (!propertyId) return;
        setLoading(true);
        setError("");
        try {
            const data = await getAuthorisedUsers(propertyId);
            // Sort: pending first, then approved, then denied
            const sorted = [...data].sort((a, b) => {
                const rank = (u) => (u.status === true ? 1 : u.reason ? 2 : 0);
                return rank(a) - rank(b);
            });
            setUsers(sorted);
            onCountChange?.(sorted.length);
        } catch (err) {
            console.error("[AuthorisedUsersList] fetch error:", err);
            setError("Failed to load authorised users.");
        } finally {
            setLoading(false);
        }
    }, [propertyId, onCountChange]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    // ── Approve ──────────────────────────────────────────────────────────────
    const handleApprove = async (user) => {
        setActionLoading((prev) => ({ ...prev, [user.id]: "approving" }));
        try {
            await approveAuthorisedUser(propertyId, user.email);
            setUsers((prev) =>
                prev.map((u) => u.id === user.id ? { ...u, status: true, reason: "" } : u)
            );
            showFeedback("success", `${user.email} approved successfully.`);
        } catch (err) {
            showFeedback("error", err.message || "Failed to approve user.");
        } finally {
            setActionLoading((prev) => ({ ...prev, [user.id]: null }));
        }
    };

    // ── Deny dialog ───────────────────────────────────────────────────────────
    const openDenyDialog = (user) => {
        setDenyTarget(user);
        setDenyReason(user.reason || "");
        setDenyDialogOpen(true);
    };

    const handleDenyConfirm = async () => {
        if (!denyTarget) return;
        setDenySubmitting(true);
        try {
            await denyAuthorisedUser(propertyId, denyTarget.email, denyReason.trim());
            setUsers((prev) =>
                prev.map((u) =>
                    u.id === denyTarget.id ? { ...u, status: false, reason: denyReason.trim() } : u
                )
            );
            showFeedback("success", `${denyTarget.email} denied.`);
            setDenyDialogOpen(false);
            setDenyTarget(null);
            setDenyReason("");
        } catch (err) {
            showFeedback("error", err.message || "Failed to deny user.");
        } finally {
            setDenySubmitting(false);
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleteSubmitting(true);
        try {
            await deleteAuthorisedUser(propertyId, deleteTarget.email);
            const updated = users.filter((u) => u.id !== deleteTarget.id);
            setUsers(updated);
            onCountChange?.(updated.length);
            showFeedback("success", `${deleteTarget.email} removed.`);
            setDeleteTarget(null);
        } catch (err) {
            showFeedback("error", err.message || "Failed to remove user.");
        } finally {
            setDeleteSubmitting(false);
        }
    };

    // ── Status helpers ────────────────────────────────────────────────────────
    const getStatusChip = (user) => {
        if (user.status === true) {
            return <Chip label="Approved" size="small" color="success" sx={{ fontWeight: 700, fontSize: 11 }} />;
        }
        if (user.reason) {
            return <Chip label="Denied" size="small" color="error" sx={{ fontWeight: 700, fontSize: 11 }} />;
        }
        return <Chip label="Pending" size="small" color="warning" sx={{ fontWeight: 700, fontSize: 11 }} />;
    };

    const formatDate = (val) => {
        if (!val) return "—";
        try {
            const d = val?.toDate ? val.toDate() : new Date(val);
            return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
        } catch { return "—"; }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">
                    Authorized Users{" "}
                    <Box component="span" sx={{ fontSize: 14, color: "text.secondary", fontWeight: 400 }}>
                        ({users.length})
                    </Box>
                </Typography>
                <Tooltip title="Refresh">
                    <IconButton onClick={fetchUsers} disabled={loading} size="small">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Inline feedback */}
            {feedback && (
                <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
                    {feedback.message}
                </Alert>
            )}

            {error && (
                <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Loading */}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : users.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                    <PersonOffIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        No authorised users added yet.
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                        Users added via the tenant dashboard will appear here for approval.
                    </Typography>
                </Box>
            ) : (
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Added</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Denial Reason</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => {
                            const isActing = actionLoading[user.id];
                            // const isPending = user.status !== true && !user.reason;
                            const isApproved = user.status === true;
                            const isDenied = !user.status && !!user.reason;

                            return (
                                <TableRow
                                    key={user.id}
                                    sx={{
                                        backgroundColor:
                                            isApproved ? "#f1f8f1"
                                                : isDenied ? "#fff5f5"
                                                    : "transparent",
                                        "&:hover": { backgroundColor: isApproved ? "#e8f5e9" : isDenied ? "#ffebee" : "#fafafa" },
                                    }}
                                >
                                    {/* User */}
                                    <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <Avatar
                                                sx={{
                                                    width: 32, height: 32, fontSize: 13, fontWeight: 700,
                                                    bgcolor: isApproved ? "#4caf50" : isDenied ? "#ef5350" : "#bdbdbd",
                                                }}
                                            >
                                                {user.email?.[0]?.toUpperCase() || "U"}
                                            </Avatar>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {user.email}
                                            </Typography>
                                        </Box>
                                    </TableCell>

                                    {/* Added */}
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(user.createdAt)}
                                        </Typography>
                                    </TableCell>

                                    {/* Status */}
                                    <TableCell>{getStatusChip(user)}</TableCell>

                                    {/* Denial reason */}
                                    <TableCell>
                                        {user.reason ? (
                                            <Typography variant="caption" color="error.main" sx={{ fontStyle: "italic" }}>
                                                {user.reason}
                                            </Typography>
                                        ) : "—"}
                                    </TableCell>

                                    {/* Actions */}
                                    <TableCell align="right">
                                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                                            {/* Approve */}
                                            {!isDenied && !isApproved && (
                                                <Tooltip title="Approve">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            disabled={!!isActing}
                                                            onClick={() => handleApprove(user)}
                                                        >
                                                            {isActing === "approving"
                                                                ? <CircularProgress size={16} color="success" />
                                                                : <ApproveIcon fontSize="small" />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}

                                            {/* Deny / Edit denial reason */}
                                            {!isApproved && !isDenied && (
                                                <Tooltip title={isDenied ? "Edit denial reason" : "Deny"}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            disabled={!!isActing}
                                                            onClick={() => openDenyDialog(user)}
                                                        >
                                                            <DenyIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}

                                            {/* Delete */}
                                            {(isApproved || isDenied) && (<Tooltip title="Remove user">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="default"
                                                        disabled={!!isActing}
                                                        onClick={() => setDeleteTarget(user)}
                                                        sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>)}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}

            {/* ── Deny Dialog ── */}
            <Dialog
                open={denyDialogOpen}
                onClose={() => !denySubmitting && setDenyDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Deny Access</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Denying access for <strong>{denyTarget?.email}</strong>. Provide a reason so the user
                        understands why their request was declined.
                    </Typography>
                    <TextField
                        label="Reason for denial"
                        value={denyReason}
                        onChange={(e) => setDenyReason(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="e.g. Lease not yet active, identity not verified…"
                        autoFocus
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDenyDialogOpen(false)} disabled={denySubmitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDenyConfirm}
                        variant="contained"
                        color="error"
                        disabled={denySubmitting || !denyReason.trim()}
                        startIcon={denySubmitting ? <CircularProgress size={16} color="inherit" /> : <DenyIcon />}
                    >
                        {denySubmitting ? "Denying…" : "Deny Access"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete Confirm Dialog ── */}
            <Dialog
                open={!!deleteTarget}
                onClose={() => !deleteSubmitting && setDeleteTarget(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Remove Authorised User</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to permanently remove{" "}
                        <strong>{deleteTarget?.email}</strong>? They will lose all access to this property.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleteSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        disabled={deleteSubmitting}
                        startIcon={deleteSubmitting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                    >
                        {deleteSubmitting ? "Removing…" : "Remove"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AuthorisedUsersList;